import { ChipOwnProps } from '@mui/material';
import { z } from 'zod/v4-mini';

import { Schema, cz } from '@/utils/zodUtils.ts';

export const conditionNameSchema = z.literal(['diabetes']);
export type ConditionName = z.infer<typeof conditionNameSchema>;
export type ConditionType = 'type1' | 'type2' | 'gestational';
export const conditionTypeSchema: Schema<ConditionType> = z.literal(
  ['type1', 'type2', 'gestational'],
  'form.required',
);
export type MedicationSchema = 'noInsulin' | 'insulinPen' | 'insulinPump';
export const medicationSchemeSchema = z.literal(
  ['noInsulin', 'insulinPen', 'insulinPump'],
  'form.required',
);

export type Condition = {
  periodStart: string;
  periodEnd?: string;
  name: ConditionName;
  type: ConditionType;
  medicationSchema: MedicationSchema;
  termDate?: string;
  isBillable: boolean;
};

export const conditionSchema: Schema<Condition> = z.object({
  periodStart: z.iso.date(),
  periodEnd: cz.optional(z.iso.date()),
  name: conditionNameSchema,
  type: conditionTypeSchema,
  medicationSchema: medicationSchemeSchema,
  termDate: cz.optional(z.iso.date()),
  isBillable: z.boolean(),
});

export const diabetesTypeTranslationMapping = (
  type: ConditionType,
  insulin: MedicationSchema,
): string => {
  switch (true) {
    case type === 'type1':
      return 'models.diabetesType.type1';
    case type === 'type2':
      return 'models.diabetesType.type2';
    case type === 'gestational' && insulin === 'noInsulin':
      return 'models.diabetesType.gestationalNoInsulin';
    default:
      return 'models.diabetesType.gestationalWithInsulin';
  }
};

export const medicationSchemaTranslationMapping = (
  schema: MedicationSchema,
): string => {
  switch (schema) {
    case 'noInsulin':
      return 'models.medicationSchema.noInsulin';
    case 'insulinPen':
      return 'models.medicationSchema.insulinPen';
    case 'insulinPump':
      return 'models.medicationSchema.insulinPump';
  }
};

export const diabetesTypeColorMapping = (
  type: ConditionType,
  insulin: MedicationSchema,
): ChipOwnProps['color'] => {
  switch (true) {
    case type === 'type1':
      return 'orange';
    case type === 'type2':
      return 'blue';
    case type === 'gestational' && insulin === 'noInsulin':
      return 'purple';
    default:
      return 'pink';
  }
};
